import React, { useState } from 'react';
import FeedbackForm from './FeedbackForm';
import FaqChat from './FaqChat';
import { useLocation, Link } from 'react-router-dom';
import { LifeBuoy, X, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { sendContactMessage } from '../api/billingApi';

/**
 * Global product-support entry (Phase-1 #4: "Product support option missing").
 * A floating "Support" button, shown only to authenticated users, that opens a
 * contact form. Reuses the existing POST /billing/contact endpoint, so messages
 * land in the same admin inbox as other contact requests — no new backend.
 */
export const SupportWidget: React.FC = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  // Chat pages have a fixed bottom composer whose send/attach controls sit at
  // the bottom-right — the same corner as this FAB. On those routes float the
  // button clear of the composer so it never covers the send button.
  const onChatComposer = /(^\/app(\/|$))|\/chat(\/|$)/.test(pathname);
  const fabBottom = onChatComposer ? 96 : 20;
  const [open, setOpen] = useState(false);
  // SRS #19: the assistant is the first stop; a human ticket is one click away.
  const [mode, setMode] = useState<'ask' | 'support' | 'feedback'>('ask');
  const [supportType, setSupportType] = useState('General question');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [attachDropped, setAttachDropped] = useState(false);

  const SUPPORT_TYPES = ['General question', 'Bug / something broken', 'Billing & subscription', 'Data / accuracy issue', 'Feature request'];

  if (!user) return null; // support is for signed-in users

  const reset = () => {
    setSupportType('General question');
    setSubject('');
    setMessage('');
    setFile(null);
    setSent(false);
    setError(null);
    setAttachDropped(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);
    try {
      // Classify the inquiry by prefixing the subject with the chosen type so it
      // routes/triages in the admin contact inbox (QA: Support Type field).
      const taggedSubject = `[${supportType}] ${subject.trim() || 'Product support request'}`;
      const res = await sendContactMessage({ subject: taggedSubject, message: message.trim(), file });
      // The message always saves; the attachment is best-effort. Let the user
      // know if their file could not be stored so they can follow up.
      if (file && res.attachment_stored === false) setAttachDropped(true);
      setSent(true);
    } catch {
      setError('Could not send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => { reset(); setOpen(true); }}
        aria-label="Contact support"
        title="Contact support"
        style={{
          position: 'fixed', right: 20, bottom: fabBottom, zIndex: 1000,
          display: 'inline-flex', alignItems: 'center', gap: 8,
          padding: '10px 16px', borderRadius: 999, border: 'none',
          background: 'var(--ds-brand-blue)', color: 'var(--ds-brand-ink, var(--ds-text-on-dark))', fontSize: 14, fontWeight: 700,
          boxShadow: '0 6px 20px rgba(31,94,255,0.35)', cursor: 'pointer',
        }}
      >
        <LifeBuoy size={18} aria-hidden />
        Support
      </button>

      {open && (
        <div
          onClick={() => setOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{ background: 'var(--color-bg-elevated, #fff)', color: 'var(--color-text, var(--ds-text))', border: '1px solid var(--color-border-subtle, var(--ds-border))', borderRadius: 14, padding: 28, maxWidth: 460, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
              <h3 style={{ margin: 0, fontSize: 18, fontWeight: 800, display: 'flex', alignItems: 'center', gap: 8 }}>
                <LifeBuoy size={18} aria-hidden /> {mode === 'ask' ? 'Ask PolicyOra' : mode === 'support' ? 'Contact Support' : 'Send Feedback'}
              </h3>
              <button type="button" onClick={() => setOpen(false)} aria-label="Close" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted, var(--ds-text-muted))' }}><X size={18} /></button>
            </div>
            <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid var(--color-border-subtle, var(--ds-border))' }}>
              {(['ask', 'support', 'feedback'] as const).map((m) => (
                <button key={m} type="button" onClick={() => setMode(m)} style={{ padding: '6px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 13, fontWeight: mode === m ? 700 : 500, color: mode === m ? 'var(--ds-text)' : 'var(--color-text-muted, var(--ds-text-muted))', borderBottom: mode === m ? '2px solid var(--ds-brand-blue)' : '2px solid transparent' }}>
                  {m === 'ask' ? 'Ask a question' : m === 'support' ? 'Need help' : 'Give feedback'}
                </button>
              ))}
            </div>

            {mode === 'ask' ? <FaqChat canEscalate onClose={() => setOpen(false)} /> : mode === 'feedback' ? <FeedbackForm /> : sent ? (
              <div style={{ textAlign: 'center', padding: '20px 0' }}>
                <div style={{ marginBottom: 10 }}><CheckCircle2 size={40} aria-hidden /></div>
                <div style={{ fontWeight: 700, fontSize: 16 }}>Message sent</div>
                <div style={{ color: 'var(--color-text-muted, var(--ds-text-muted))', fontSize: 13, marginTop: 4 }}>Our team will respond within 1–2 business days.</div>
                {attachDropped && (
                  <div style={{ color: 'var(--ds-warning-text)', background: 'var(--ds-warning-bg)', border: '1px solid #fedf89', borderRadius: 8, padding: '8px 10px', fontSize: 12, marginTop: 10 }}>
                    Your message was sent, but the attachment couldn't be stored. Please include the key details in the message, or email the file to us.
                  </div>
                )}
                <div style={{ marginTop: 18, display: 'flex', gap: 10, justifyContent: 'center' }}>
                  <Link to="/support" onClick={() => setOpen(false)} style={{ padding: '10px 20px', background: 'transparent', color: 'var(--color-text, var(--ds-text))', border: '1px solid var(--color-border-subtle, var(--ds-border))', borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontSize: 14 }}>View my requests</Link>
                  <button type="button" onClick={() => setOpen(false)} style={{ padding: '10px 28px', background: '#101828', color: 'var(--ds-text-on-dark)', border: 'none', borderRadius: 8, fontWeight: 700, cursor: 'pointer' }}>Close</button>
                </div>
              </div>
            ) : (
              <form onSubmit={submit}>
                <p style={{ margin: '0 0 16px', color: 'var(--color-text-muted, var(--ds-text-muted))', fontSize: 14 }}>
                  Need help or found an issue? Send us a message and we'll get back to you.
                </p>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Support type</label>
                  <select
                    value={supportType} onChange={e => setSupportType(e.target.value)}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--color-border-subtle, var(--ds-border))', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, var(--ds-text))' }}
                  >
                    {SUPPORT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Subject</label>
                  <input
                    type="text" value={subject} onChange={e => setSubject(e.target.value)}
                    placeholder="Brief summary"
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--color-border-subtle, var(--ds-border))', fontSize: 14, outline: 'none', boxSizing: 'border-box', background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, var(--ds-text))' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>Message</label>
                  <textarea
                    required value={message} onChange={e => setMessage(e.target.value)}
                    placeholder="Describe your question or the problem you're seeing…"
                    rows={5}
                    style={{ width: '100%', padding: '8px 12px', borderRadius: 8, border: '1.5px solid var(--color-border-subtle, var(--ds-border))', fontSize: 14, outline: 'none', resize: 'vertical', boxSizing: 'border-box', background: 'var(--color-input-bg, #fff)', color: 'var(--color-text, var(--ds-text))' }}
                  />
                </div>
                <div style={{ marginBottom: 16 }}>
                  <label style={{ display: 'block', fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    Attachment <span style={{ fontWeight: 400, color: 'var(--color-text-muted, var(--ds-text-muted))' }}>(optional, max 10 MB)</span>
                  </label>
                  <input
                    type="file"
                    onChange={e => setFile(e.target.files?.[0] ?? null)}
                    accept=".pdf,.doc,.docx,.txt,.csv,.xlsx,.png,.jpg,.jpeg"
                    style={{ width: '100%', fontSize: 13, color: 'var(--color-text, var(--ds-text))' }}
                  />
                  {file && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-muted, var(--ds-text-muted))', marginTop: 4 }}>
                      {file.name} · {(file.size / 1024 / 1024).toFixed(2)} MB
                    </div>
                  )}
                </div>
                {error && <div style={{ color: 'var(--ds-danger-text)', fontSize: 13, marginBottom: 10 }}>{error}</div>}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button type="button" onClick={() => setOpen(false)} style={{ flex: 1, padding: 10, background: 'transparent', border: '1px solid var(--color-border-subtle, var(--ds-border))', borderRadius: 8, fontSize: 14, cursor: 'pointer', color: 'var(--color-text, var(--ds-text))' }}>Cancel</button>
                  <button type="submit" disabled={sending} style={{ flex: 1, padding: 10, background: 'var(--ds-brand-blue)', color: 'var(--ds-brand-ink, var(--ds-text-on-dark))', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 700, cursor: 'pointer', opacity: sending ? 0.7 : 1 }}>
                    {sending ? 'Sending…' : 'Send message'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default SupportWidget;
